import { Clear, PersonAdd } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Tooltip,
  Typography,
} from '@mui/material'
import { deleteDoc, doc } from 'firebase/firestore'
import { useState } from 'react'
import { firestore } from '../core'
import { useAuth } from '../core/providers/auth'
import { useUserHasWorkoutEditPermission } from '../hooks/permissions'
import { WaitlistEntry, Workout } from '../providers/workouts'
import { BookingError, promoteWaitlistEntryToParticipant } from '../lib/booking'
import { features } from '../core/config/features'

type Props = {
  workout: Workout
}

const WaitlistList = ({ workout }: Props) => {
  const { user, admin } = useAuth()
  const editPermission = useUserHasWorkoutEditPermission(workout)
  const canSeeFullWaitlist = editPermission
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const [promoteLoadingId, setPromoteLoadingId] = useState<string | null>(null)
  const { waitlist = [], id } = workout
  const spotsAvailable = workout.participantsCount < workout.numberOfPlaces
  const workoutCredits = features.credits ? Number(workout.credits ?? 0) : 0

  const handleRemoveWaitlistEntry = async (uid: string) => {
    setLoadingId(uid)
    try {
      await deleteDoc(doc(firestore, 'workouts', id, 'waitlist', uid))
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingId(null)
    }
  }

  const handlePromoteWaitlistEntry = async (entry: WaitlistEntry) => {
    setPromoteLoadingId(entry.uid)
    try {
      await promoteWaitlistEntryToParticipant(firestore, id, entry, {
        creditsEnabled: features.credits,
        participantsCount: workout.participantsCount,
        numberOfPlaces: workout.numberOfPlaces,
      })
    } catch (error) {
      console.log(
        error instanceof BookingError
          ? error.message
          : 'Det gick inte att flytta in från kön.'
      )
    } finally {
      setPromoteLoadingId(null)
    }
  }

  if (waitlist.length === 0) {
    return null
  }

  return (
    <List
      subheader={
        <ListSubheader
          disableSticky
          sx={{ padding: 0, backgroundColor: 'transparent' }}
        >
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
            {canSeeFullWaitlist ? `Kö (${waitlist.length})` : 'Din köplats'}
          </Typography>
        </ListSubheader>
      }
    >
      {waitlist.map((entry, index) => {
        const canRemove = editPermission || entry.uid === user?.uid

        return (
          <ListItem key={entry.uid} sx={{ paddingX: 0 }}>
            <ListItemText
              primary={
                canSeeFullWaitlist
                  ? `${index + 1}. ${entry.displayName}`
                  : 'Du står i kön'
              }
              secondary={editPermission && (entry.phone || entry.email)}
            />
            {canRemove && (
              <ListItemSecondaryAction>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {editPermission && (
                    <Tooltip
                      title={
                        !spotsAvailable
                          ? 'Passet är fullt'
                          : workoutCredits > 0 && !admin
                          ? 'Endast admin kan flytta in när passet kostar credits'
                          : 'Flytta in från kö'
                      }
                    >
                      <span>
                        <IconButton
                          disabled={
                            !spotsAvailable || (workoutCredits > 0 && !admin)
                          }
                          onClick={() => handlePromoteWaitlistEntry(entry)}
                        >
                          {promoteLoadingId === entry.uid ? (
                            <CircularProgress size={25} />
                          ) : (
                            <PersonAdd color="primary" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="Ta bort från kö">
                    <IconButton
                      onClick={() => handleRemoveWaitlistEntry(entry.uid)}
                    >
                      {loadingId === entry.uid ? (
                        <CircularProgress size={25} />
                      ) : (
                        <Clear color="error" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItemSecondaryAction>
            )}
          </ListItem>
        )
      })}
    </List>
  )
}

export default WaitlistList
